/* eslint-disable no-console, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-unused-vars, no-await-in-loop, no-nested-ternary, no-continue */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Collapse,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  GitCompareArrows,
  Bug,
  Dices,
} from 'lucide-react';
import { useNotify } from 'react-admin';
import {
  getMediaUrlByPath,
  parseDeliverySpec,
  renderDeliveryPlan,
  stripCitationMarkers,
} from '@musedrops/shared';
// import { getShowDetailsImageUrl } from '@musedrops/shared'; // Replaced with direct optimization URLs
import type { NarrativeForm } from '@musedrops/shared';
import {
  PromptSection as PromptSectionType,
  SectionData,
  PromptConfig,
  PromptDebuggerShow,
} from '../types';
import { ModelPicker } from './ModelPicker';
import { ModelComparisonDialog } from './ModelComparisonDialog';
import { ModelService } from '../../../services/modelService';
import { PromptTextArea } from './PromptTextArea';
import { ResponseDisplay } from './ResponseDisplay';
import { ShowIdeaSection } from './ShowIdeaSection';
import { StoryIdeaSection } from './StoryIdeaSection';
import { usePromptDebug } from '../../../hooks/usePromptDebug';
import { supabase } from '../../../services/supabase';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { PromptService } from '../services/promptService';
import { GenreApi } from '../../../services/genreApi';
import { LanguageApi } from '../../../services/languageApi';
import { listAllNarrativeForms } from '../../../data/narrativeForms';
import {
  extractTemplateParams,
  getParamAvailability,
} from '../utils/paramUtils';

/**
 * Add cache-busting parameter to URL, using correct separator
 */
const addCacheBusting = (url: string): string => {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
};

/**
 * Get direct optimization URL for image display
 * Uses Cloud Run generation service for temporary debug images
 * Constructs URL like: {CLOUD_RUN_GEN_SERVICE_URL}/image/media/temp/debug-console-image.png?size=300&format=webp
 */
const getDirectImageOptimizationUrl = (
  imageUrl: string,
  size: number = 300
): string => {
  if (!imageUrl) return imageUrl;

  try {
    // Extract file path from Supabase storage URL
    // Expected format: {SUPABASE_URL}/storage/v1/object/public/media/temp/debug-console-image.png
    const storagePattern = /\/storage\/v1\/object\/public\/(.+)$/;
    const match = imageUrl.match(storagePattern);

    if (!match) {
      // If it's not a Supabase storage URL, return as-is (fallback for OpenAI URLs)
      return imageUrl;
    }

    const filePath = match[1]; // e.g., "media/temp/debug-console-image.png"

    // Construct the optimization URL using Cloud Run generation service
    const cloudRunUrl = import.meta.env.VITE_CLOUD_RUN_GEN_SERVICE_URL;
    return `${cloudRunUrl}/image/${filePath}?size=${size}&format=webp`;
  } catch (error) {
    console.warn(
      'Failed to construct optimization URL, falling back to original:',
      error
    );
    return imageUrl;
  }
};

interface PromptSectionProps {
  section: PromptSectionType;
  sectionData: SectionData;
  onDataChange: (sectionId: keyof SectionData, data: unknown) => void;
  onOpenSyntaxGuide?: () => void;
}

export const PromptSection: React.FC<PromptSectionProps> = ({
  section,
  sectionData,
  onDataChange,
  onOpenSyntaxGuide,
}) => {
  // Use genPrompt ID (key in gen_prompts table) - fail fast if missing
  const genPromptKey = section.promptConfig?.id || section.genPromptId;
  if (!genPromptKey) {
    throw new Error(
      `Section ${section.id} missing promptConfig.id or genPromptId`
    );
  }
  const promptDebug = usePromptDebug(genPromptKey);
  const notify = useNotify();

  const [isExpanded, setIsExpanded] = useState(true);
  const [currentModelName, setCurrentModelName] = useState<string>('');
  const [originalModelName, setOriginalModelName] = useState<string>('');
  const [availableModels, setAvailableModels] = useState<
    Array<{ key: string; name: string }>
  >([]);

  const [currentPromptContent, setCurrentPromptContent] = useState<string>('');
  const [originalPromptContent, setOriginalPromptContent] =
    useState<string>('');
  const [availableStories, setAvailableStories] = useState<
    Array<{ id: string; title: string }>
  >([]);
  const [availableShows, setAvailableShows] = useState<PromptDebuggerShow[]>(
    []
  );
  const [availableGenres, setAvailableGenres] = useState<
    Array<{
      id: string;
      name: string;
      description?: string;
      instructions?: string;
    }>
  >([]);
  const [availableLanguages, setAvailableLanguages] = useState<
    Array<{
      key: string;
      name: string;
    }>
  >([]);
  const [existingTopics, setExistingTopics] =
    useState<string>('No existing shows');
  // #766: narrative forms for the Story Outline form selector (input above prompt)
  const [availableForms, setAvailableForms] = useState<NarrativeForm[]>([]);

  // Model comparison dialog state
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  // Debug mode info state
  const [debugModeInfo, setDebugModeInfo] = useState<{
    isEnabled: boolean;
    debugPreset: { key: string; name: string } | null;
  }>({ isEnabled: false, debugPreset: null });

  // Load prompt content from database on mount - only depend on static props
  // CRITICAL: Dependencies must be stable to prevent re-renders during typing
  // This fixes the bug where typed characters appear momentarily then get deleted
  useEffect(() => {
    if (section.hasPrompt && section.promptConfig?.id) {
      promptDebug.loadPromptContent().then(content => {
        if (content) {
          setCurrentPromptContent(content);
          setOriginalPromptContent(content);
        } else {
          // Set default prompt if none exists in config
          const defaultPrompt = `You are an AI assistant helping to generate ${section.title.toLowerCase()} content.\n\nPlease provide a detailed response.`;
          setCurrentPromptContent(defaultPrompt);
          setOriginalPromptContent(defaultPrompt);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // CRITICAL: Only include truly static dependencies here
    // Do NOT add promptDebug as it can change and cause re-renders during typing
    section.hasPrompt,
    section.promptConfig?.id,
    // Note: section.promptConfig?.requiredParams and section.title removed
    // as they are static for each section and don't need to trigger re-loads
  ]);

  // Load available models and current model selection on mount
  useEffect(() => {
    const loadModelsAndConfig = async () => {
      try {
        // Determine preset type based on section
        let presetType: string;
        let models;
        if (
          section.id === 'imageGeneration' ||
          section.id === 'storyImageGeneration'
        ) {
          // For image generation sections, load image models only
          presetType = 'image';
          const allModels = await ModelService.getAvailableModels();
          models = allModels.filter(model => model.type === 'image');
        } else if (section.id === 'tts' || section.id === 'showAnnounce' || section.id === 'storyAnnounce') {
          // For TTS, Show Announce, and Story Announce sections, load voice models
          presetType = 'voice';
          models = await ModelService.getAvailableVoiceModels();
        } else {
          // For other sections, load text models
          presetType = 'text';
          models = await ModelService.getAvailableTextModels();
        }
        setAvailableModels(models);

        // Load debug mode info for this preset type
        const debugInfo = await ModelService.getDebugModeInfo(presetType);
        setDebugModeInfo(debugInfo);

        // Load current model selection from database using genPrompt key
        const promptKey = section.promptConfig?.id || section.genPromptId;
        if (!promptKey) {
          throw new Error(
            `Section ${section.id} missing promptConfig.id or genPromptId`
          );
        }
        const modelName = await ModelService.loadModelConfig(promptKey);

        if (modelName) {
          setCurrentModelName(modelName);
          setOriginalModelName(modelName);
        } else {
          // Fail fast - no model configuration found
          console.error(
            `No model configuration found for prompt: ${promptKey}`
          );
          throw new Error(
            `No model configuration found for prompt: ${promptKey}. Please configure a model preset in the database.`
          );
        }
      } catch (error) {
        console.error('Failed to load models:', error);
        setAvailableModels([]);
      }
    };

    loadModelsAndConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // CRITICAL: Only include truly static dependencies
    section.genPromptId,
    section.id,
  ]);

  // Listen for debug mode changes from the header toggle
  useEffect(() => {
    const handleDebugModeChange = async () => {
      // Determine preset type based on section
      let presetType: string;
      if (
        section.id === 'imageGeneration' ||
        section.id === 'storyImageGeneration'
      ) {
        presetType = 'image';
      } else if (section.id === 'tts' || section.id === 'showAnnounce' || section.id === 'storyAnnounce') {
        // For TTS, Show Announce, and Story Announce sections, use voice preset type
        presetType = 'voice';
      } else {
        presetType = 'text';
      }
      // Refresh debug mode info
      const debugInfo = await ModelService.getDebugModeInfo(presetType);
      setDebugModeInfo(debugInfo);
    };

    window.addEventListener('debugModeChanged', handleDebugModeChange);
    return () =>
      window.removeEventListener('debugModeChanged', handleDebugModeChange);
  }, [section.id]);

  // No validation needed for model picker - models are pre-validated

  // CRITICAL: Memoize params with stable dependencies to prevent re-renders during typing
  const availableParams = useMemo(() => {
    // Genre details are stored in sectionData when genre is selected via handleGenreChange
    const genreName = sectionData.showIdeas?.genreName || '';
    const genreDescription = sectionData.showIdeas?.genreDescription || '';
    const genreInstructions = sectionData.showIdeas?.genreInstructions || '';

    // Language details are stored in sectionData when language is selected via handleLanguageChange
    const languageName = sectionData.showIdeas?.languageName || '';
    console.log(
      '📊 [PARAMS] availableParams recalculating. languageName from sectionData:',
      sectionData.showIdeas?.languageName
    );

    return {
      'show-topic': sectionData.showIdeas?.topic || '',
      'show-name': sectionData.showTranslation?.name || '', // Show name from show translation (for announcements)
      'show-style': sectionData.show?.style || '',
      showTags: sectionData.show?.tags || '',
      'show-audio-logo': sectionData.showTranslation?.audioLogoDesc || '', // Audio logo from show translation
      'show-story-scope': sectionData.show?.storyScope || '',
      'title-instructions': sectionData.show?.titleInstructions || '',
      'story-topic': sectionData.storyIdea?.topic || '',
      'story-title': sectionData.storyIdea?.title || '',
      'story-brief': sectionData.storyIdea?.brief || '',
      'story-takeaway': sectionData.storyIdea?.takeaway || '',
      'story-description': sectionData.storyIdea?.description || '',
      // Issue #643: Story outline parameter
      'story-outline': sectionData.storyOutline?.outline || '',
      // #766 Phase 2/3: delivery spec emitted by the outline step, consumed by
      // the story_script prompt as {{story-delivery-spec}}. Rendered into the
      // same delivery-plan text the pipeline produces (parse sanitizes +
      // renderDeliveryPlan converts shares → absolute word budgets); falls
      // back to the raw JSON only if the spec doesn't parse.
      'story-delivery-spec': (() => {
        const rawSpec = sectionData.storyOutline?.delivery_spec;
        if (!rawSpec) return '';
        try {
          const specValue =
            typeof rawSpec === 'string' ? JSON.parse(rawSpec) : rawSpec;
          const spec = parseDeliverySpec(specValue);
          if (spec) return renderDeliveryPlan(spec, 500); // DEFAULT_TARGET_WORDS
        } catch {
          // fall through to raw passthrough
        }
        return typeof rawSpec === 'string'
          ? rawSpec
          : JSON.stringify(rawSpec, null, 2);
      })(),
      'script-content': sectionData.script?.script || '',
      'voice-profile-1': sectionData.show?.voiceProfile1 || '',
      voiceIndex1: sectionData.show?.voiceIndex1 ?? null,
      'voice-instructions-1': sectionData.show?.voiceInstructions1 || '',
      'voice-profile-2': sectionData.show?.voiceProfile2 || '',
      voiceIndex2: sectionData.show?.voiceIndex2 ?? null,
      'voice-instructions-2': sectionData.show?.voiceInstructions2 || '',
      'script-instructions-1': sectionData.show?.scriptInstructions1 || '',
      'script-instructions-2': sectionData.show?.scriptInstructions2 || '',
      // #766 Phase 2: the outline prompt's host params — same persona-resolved
      // instructions as script-instructions-*, under the pipeline's param names
      // (gen-story-outline resolveHostInstructions).
      'host-instructions-1': sectionData.show?.scriptInstructions1 || '',
      'host-instructions-2': sectionData.show?.scriptInstructions2 || '',
      // #766 Phase 3: rolled narrative form — selected via the Story Outline
      // form dropdown (code-rolled in the pipeline, user/dice-rolled here).
      // Only the instructions feed the prompt; the key is UI selection state
      // (the pipeline stores it on stories.form_key, never in the prompt).
      'form-instructions': sectionData.storyOutline?.formInstructions || '',
      // #766: matches DEFAULT_TARGET_WORDS in gen-story-outline/gen-story-script
      'target-words': '500',
      'personas-list': sectionData.show?.personasList || '',
      'dynamics-list': sectionData.show?.dynamicsList || '',
      'dynamics-instructions': sectionData.show?.dynamicsInstructions || '',
      'show-image-prompt': sectionData.show?.imagePrompt || '',
      'story-image-prompt': sectionData.storyIdea?.imagePrompt || '',
      genre: genreName,
      'genre-name': genreName,
      'genre-description': genreDescription,
      'genre-instructions': genreInstructions,
      language: languageName,
      'idea-count': '2', // Default for prompt debugger; backend may override
      'ideas-count': '2', // Default for story ideas
      'past-stories': 'This is a new show with no past stories yet.', // Default for new shows
      'existing-topics': existingTopics, // Populated with actual show topics
    };
  }, [
    // CRITICAL: These dependencies should only change when user explicitly changes data
    // not during typing in prompt text areas
    sectionData.showIdeas?.topic,
    sectionData.showTranslation?.name, // Show name from show translation
    sectionData.showTranslation?.audioLogoDesc, // Audio logo from show translation
    sectionData.show?.style,
    sectionData.show?.tags,
    sectionData.show?.storyScope,
    sectionData.show?.titleInstructions,
    sectionData.storyIdea?.topic,
    sectionData.storyIdea?.title,
    sectionData.storyIdea?.brief,
    sectionData.storyIdea?.takeaway,
    sectionData.storyIdea?.description,
    // Issue #643: Story outline dependency
    sectionData.storyOutline?.outline,
    // #766: delivery spec + rolled form
    sectionData.storyOutline?.delivery_spec,
    sectionData.storyOutline?.formInstructions,
    sectionData.script?.script,
    sectionData.show?.voiceProfile1,
    sectionData.show?.voiceIndex1,
    sectionData.show?.voiceInstructions1,
    sectionData.show?.voiceProfile2,
    sectionData.show?.voiceIndex2,
    sectionData.show?.voiceInstructions2,
    sectionData.show?.scriptInstructions1,
    sectionData.show?.scriptInstructions2,
    sectionData.show?.personasList,
    sectionData.show?.dynamicsList,
    sectionData.show?.dynamicsInstructions,
    sectionData.show?.imagePrompt,
    sectionData.storyIdea?.imagePrompt,
    sectionData.showIdeas?.genreName,
    sectionData.showIdeas?.genreDescription,
    sectionData.showIdeas?.genreInstructions,
    sectionData.showIdeas?.languageName,
    existingTopics,
  ]);

  const handleModelChange = useCallback((modelName: string) => {
    setCurrentModelName(modelName);
  }, []);

  const handlePromptChange = useCallback((content: string) => {
    setCurrentPromptContent(content);
  }, []);

  const handleModelReset = useCallback(() => {
    setCurrentModelName(originalModelName);
  }, [originalModelName]);

  const handleModelSave = useCallback(async () => {
    try {
      const genPromptKey = section.promptConfig?.id || section.genPromptId;
      if (!genPromptKey) {
        throw new Error(
          `Section ${section.id} missing promptConfig.id or genPromptId`
        );
      }
      await ModelService.saveModelConfig(genPromptKey, currentModelName);
      setOriginalModelName(currentModelName);
    } catch (error) {
      console.error('Failed to save model config:', error);
    }
  }, [
    section.promptConfig?.id,
    section.genPromptId,
    currentModelName,
    section.id,
  ]);

  const handlePromptReset = useCallback(() => {
    setCurrentPromptContent(originalPromptContent);
  }, [originalPromptContent]);

  const handlePromptSave = useCallback(() => {
    if (section.promptConfig?.id) {
      const configToSave: PromptConfig = {
        id: section.promptConfig.id,
        content: currentPromptContent,
        modelName: currentModelName,
      };
      promptDebug.savePromptConfig(configToSave);
      // Update original content to reflect the saved state
      setOriginalPromptContent(currentPromptContent);
    }
  }, [
    section.promptConfig?.id,
    currentPromptContent,
    currentModelName,
    promptDebug,
  ]);

  // Add local loading state for TTS section
  const [ttsLoading, setTtsLoading] = useState(false);
  const [showAnnounceLoading, setShowAnnounceLoading] = useState(false); // Separate loading state for Show Announce
  const [storyAnnounceLoading, setStoryAnnounceLoading] = useState(false); // Separate loading state for Story Announce

  // Add local loading state for text generation (queue-based)
  const [textLoading, setTextLoading] = useState(false);

  // Add local loading state for image generation (queue-based)
  const [imageLoading, setImageLoading] = useState(false);

  // Queue job subscription for async text generation
  const { watchJob: watchTextJob } = useQueueJob({
    timeout: 5 * 60 * 1000, // 5 minutes for text generation
    onComplete: (output: any) => {
      console.log('✅ [TEXT] Queue job completed:', output);
      console.log('✅ [TEXT] section.id:', section.id);
      // The output contains the generated text/JSON result
      const result = output?.result || output;
      console.log('✅ [TEXT] result:', result);
      if (result) {
        // Parse and process the result similar to synchronous flow
        let responseData: any = result;

        // Special handling for show profile section
        if (section.id === 'show' && responseData) {
          const processedShowData = {
            ...responseData,
            voiceProfile1: responseData.voiceProfile1 || '',
            voiceInstructions1: responseData.voiceInstructions1 || '',
            voiceProfile2: responseData.voiceProfile2 || '',
            voiceInstructions2: responseData.voiceInstructions2 || '',
            // Store raw response for JSON view
            rawResponse: result,
          };
          onDataChange(section.id, processedShowData);
        } else if (section.id === 'showIdeas' && responseData) {
          // Handle show ideas
          console.log('✅ [TEXT] Processing showIdeas section');
          let showIdeas: any[] = [];
          if (responseData.shows && Array.isArray(responseData.shows)) {
            showIdeas = responseData.shows;
            console.log('✅ [TEXT] Extracted showIdeas:', showIdeas);
          }
          // Extract topic from first idea to include in same update (prevents stale closure bug)
          const firstIdea = showIdeas[0];
          const extractedTopic =
            firstIdea && typeof firstIdea === 'object' && 'topic' in firstIdea
              ? firstIdea.topic
              : null;

          const currentData = sectionData.showIdeas || {};
          const updatedData = {
            ...currentData,
            ideas: showIdeas,
            // Include topic in same update if extracted, preserving existing topic otherwise
            ...(extractedTopic ? { topic: extractedTopic } : {}),
            // Store raw response for JSON view
            rawResponse: result,
          };
          console.log('✅ [TEXT] Calling onDataChange with:', updatedData);
          onDataChange('showIdeas', updatedData);
        } else if (section.id === 'storyIdea' && responseData) {
          // Handle story ideas - extract first story
          if (responseData.stories && Array.isArray(responseData.stories)) {
            const firstStory = responseData.stories[0];
            responseData = firstStory
              ? {
                  topic: firstStory.topic || '',
                  title: firstStory.title || '',
                  slug: firstStory.slug || '',
                  subtitle: firstStory.subtitle || '',
                  brief: firstStory.brief || '',
                  takeaway: firstStory.takeaway || '',
                  taxonomy: firstStory.taxonomy || '',
                  description: firstStory.description || '',
                  searchTerms: firstStory.searchTerms || '',
                  imagePrompt: firstStory.imagePrompt || '',
                  // Store raw response for JSON view
                  rawResponse: result,
                }
              : {};
          }
          onDataChange(section.id, responseData);
        } else if (section.id === 'storyOutline' && responseData) {
          // Mirror gen-story-outline-callback: search-grounded presets inject
          // citation markers ("[3, 6]") that the prompt cannot fully suppress —
          // strip them from the outline before it feeds display + downstream
          // params. (delivery_spec is sanitized by parseDeliverySpec when the
          // script param is rendered.)
          const outlineText =
            typeof responseData.outline === 'string'
              ? stripCitationMarkers(responseData.outline)
              : responseData.outline;
          onDataChange('storyOutline', {
            ...sectionData.storyOutline,
            ...responseData,
            outline: outlineText,
            rawResponse: result,
          });
        } else if (section.id === 'script' && responseData) {
          // Handle script - extract script value
          const scriptContent =
            typeof responseData === 'string'
              ? responseData
              : responseData.script || '';
          onDataChange('script', {
            script: scriptContent,
            rawResponse: result,
          });
        } else {
          // Generic handler - store raw response for JSON view
          onDataChange(section.id, { ...responseData, rawResponse: result });
        }
        notify('Generation completed successfully', { type: 'success' });
      } else {
        console.error('❌ [TEXT] No result in job output:', output);
        notify('Generation completed but no result found', { type: 'error' });
      }
      setTextLoading(false);
    },
    onError: error => {
      console.error('❌ [TEXT] Queue job failed:', error);
      notify(`Generation failed: ${error}`, { type: 'error' });
      setTextLoading(false);
    },
    onTimeout: () => {
      console.error('❌ [TEXT] Queue job timed out');
      notify('Generation timed out after 5 minutes', { type: 'error' });
      setTextLoading(false);
    },
  });

  // Queue job subscription for async image generation
  const { watchJob: watchImageJob } = useQueueJob({
    timeout: 5 * 60 * 1000, // 5 minutes for image generation
    onComplete: (output: any) => {
      console.log('✅ [IMAGE] Queue job completed:', output);
      // Check for imageUrl, url, or path (convert path to full URL)
      let imageUrl = output?.imageUrl || output?.url;
      if (!imageUrl && output?.path) {
        imageUrl = getMediaUrlByPath(output.path);
      }
      if (imageUrl) {
        // Update the appropriate section based on current section
        const sectionKey =
          section.id === 'storyImageGeneration'
            ? 'storyImageGeneration'
            : 'imageGeneration';
        onDataChange(sectionKey, { imageUrl });
        notify('Image generated successfully', { type: 'success' });
      } else {
        console.error('❌ [IMAGE] No image URL in job output:', output);
        notify('Image generation completed but no image URL found', {
          type: 'error',
        });
      }
      setImageLoading(false);
    },
    onError: error => {
      console.error('❌ [IMAGE] Queue job failed:', error);
      notify(`Image generation failed: ${error}`, { type: 'error' });
      setImageLoading(false);
    },
    onTimeout: () => {
      console.error('❌ [IMAGE] Queue job timed out');
      notify('Image generation timed out after 5 minutes', { type: 'error' });
      setImageLoading(false);
    },
  });

  // Queue job subscription for async TTS, Show Announce, and Story Announce generation (reused for all)
  const { watchJob: watchTtsJob, status: ttsJobStatus } = useQueueJob({
    timeout: 10 * 60 * 1000, // 10 minutes for TTS/Announce
    onComplete: output => {
      const sectionName =
        section.id === 'showAnnounce' ? 'SHOW_ANNOUNCE' :
        section.id === 'storyAnnounce' ? 'STORY_ANNOUNCE' : 'TTS';
      console.log(
        `✅ [${sectionName}] Queue job completed:`,
        output
      );
      // Check for audioUrl or path (convert path to full URL)
      let audioUrl = output?.audioUrl as string;
      if (!audioUrl && output?.path) {
        audioUrl = getMediaUrlByPath(output.path);
      }
      if (audioUrl) {
        // Add cache-busting parameter for audio URLs
        const timestamp = Date.now();
        // Update the correct section based on which one is active
        const targetSection =
          section.id === 'showAnnounce' ? 'showAnnounce' :
          section.id === 'storyAnnounce' ? 'storyAnnounce' : 'tts';
        onDataChange(targetSection, { audioUrl: `${audioUrl}?t=${timestamp}` });
      } else {
        console.error(
          `❌ [${sectionName}] No audio URL in job output:`,
          output
        );
        const displayName =
          section.id === 'showAnnounce' ? 'Show Announce' :
          section.id === 'storyAnnounce' ? 'Story Announce' : 'TTS';
        notify(
          `${displayName} generation completed but no audio URL found`,
          {
            type: 'error',
          }
        );
      }
      // Use correct loading setter based on section
      if (section.id === 'showAnnounce') {
        setShowAnnounceLoading(false);
      } else if (section.id === 'storyAnnounce') {
        setStoryAnnounceLoading(false);
      } else {
        setTtsLoading(false);
      }
    },
    onError: error => {
      const sectionName =
        section.id === 'showAnnounce' ? 'Show Announce' :
        section.id === 'storyAnnounce' ? 'Story Announce' : 'TTS';
      console.error(
        `❌ [${sectionName.toUpperCase().replace(' ', '_')}] Queue job failed:`,
        error
      );
      notify(`${sectionName} generation failed: ${error}`, { type: 'error' });
      // Use correct loading setter based on section
      if (section.id === 'showAnnounce') {
        setShowAnnounceLoading(false);
      } else if (section.id === 'storyAnnounce') {
        setStoryAnnounceLoading(false);
      } else {
        setTtsLoading(false);
      }
    },
    onTimeout: () => {
      const sectionName =
        section.id === 'showAnnounce' ? 'Show Announce' :
        section.id === 'storyAnnounce' ? 'Story Announce' : 'TTS';
      console.error(`❌ [${sectionName.toUpperCase().replace(' ', '_')}] Queue job timed out`);
      notify(`${sectionName} generation timed out after 10 minutes`, {
        type: 'error',
      });
      // Use correct loading setter based on section
      if (section.id === 'showAnnounce') {
        setShowAnnounceLoading(false);
      } else if (section.id === 'storyAnnounce') {
        setStoryAnnounceLoading(false);
      } else {
        setTtsLoading(false);
      }
    },
  });

  const handleRun = async () => {
    // No pre-flight validation needed - parameters are optional
    // The run handler will proceed even if some parameters are missing

    // Special handling for TTS section - use queue-based async pattern
    if (section.id === 'tts') {
      const ttsParams = {
        text: sectionData.script?.script || '',
        script: sectionData.script?.script || '',
        'script-content': sectionData.script?.script || '',
        voiceProfile1: availableParams['voice-profile-1'] || '',
        voiceIndex1: availableParams.voiceIndex1 ?? null,
        voiceProfile2: availableParams['voice-profile-2'] || '',
        voiceIndex2: availableParams.voiceIndex2 ?? null,
        voiceInstructions1: availableParams['voice-instructions-1'] || '',
        voiceInstructions2: availableParams['voice-instructions-2'] || '',
        modelPreset: currentModelName,
      };

      try {
        setTtsLoading(true);

        // Create queue job and watch for completion via real-time subscription
        const result = await PromptService.createTTSJob(ttsParams);

        if (!result.success || !result.jobId) {
          throw new Error(result.error || 'Failed to create TTS job');
        }

        console.log(
          '🎙️ [TTS] Queue job created, watching for completion:',
          result.jobId
        );

        // Start watching the job via real-time subscription
        watchTtsJob(result.jobId);

        // Note: setTtsLoading(false) will be called by the hook's onComplete/onError/onTimeout
      } catch (error) {
        console.error('❌ [TTS] Unexpected error:', error);
        notify(
          `TTS generation failed: ${error instanceof Error ? error.message : 'Unexpected error occurred'}`,
          { type: 'error' }
        );
        setTtsLoading(false);
      }

      return; // Skip the normal prompt testing flow
    }

    // Special handling for Show Announce section - use queue-based async pattern
    // Similar to debug-gen-tts, uses UI parameters directly (not database lookups)
    if (section.id === 'showAnnounce') {
      const showAnnounceParams = {
        'show-audio-logo': availableParams['show-audio-logo'] || '',
        'show-name': availableParams['show-name'] || '',
        'voice-profile-1': availableParams['voice-profile-1'] || '',
        modelPreset: currentModelName,
        lang: availableParams.lang || 'en',
      };

      // Validate required parameters (same as what the Edge Function validates)
      if (
        !showAnnounceParams['show-name'] ||
        !showAnnounceParams['show-audio-logo'] ||
        !showAnnounceParams['voice-profile-1']
      ) {
        notify(
          'Show Announce generation failed: Missing required parameters (show-name, show-audio-logo, voice-profile-1)',
          {
            type: 'error',
          }
        );
        return;
      }

      try {
        setShowAnnounceLoading(true); // Use dedicated loading state

        // Create queue job and watch for completion via real-time subscription
        const result =
          await PromptService.createShowAnnounceJob(showAnnounceParams);

        if (!result.success || !result.jobId) {
          throw new Error(result.error || 'Failed to create Show Announce job');
        }

        console.log(
          '📢 [SHOW_ANNOUNCE] Queue job created, watching for completion:',
          result.jobId
        );

        // Start watching the job via real-time subscription
        watchTtsJob(result.jobId); // Reuse TTS job watcher

        // Note: setShowAnnounceLoading(false) will be called by the hook's onComplete/onError/onTimeout
      } catch (error) {
        console.error('❌ [SHOW_ANNOUNCE] Unexpected error:', error);
        notify(
          `Show Announce generation failed: ${error instanceof Error ? error.message : 'Unexpected error occurred'}`,
          { type: 'error' }
        );
        setShowAnnounceLoading(false);
      }

      return; // Skip the normal prompt testing flow
    }

    // Special handling for Story Announce section - use queue-based async pattern
    // Similar to debug-gen-show-announce, uses UI parameters directly (not database lookups)
    if (section.id === 'storyAnnounce') {
      const storyAnnounceParams = {
        // Check storyTranslation first (from Story: Translation section), then storyIdea, then availableParams
        'story-title': sectionData.storyTranslation?.title || sectionData.storyIdea?.title || availableParams['story-title'] || '',
        'show-name': sectionData.showTranslation?.name || availableParams['show-name'] || '',
        'voice-profile-1': availableParams['voice-profile-1'] || '',
        modelPreset: currentModelName,
      };

      // Validate required parameters (same as what the Edge Function validates)
      if (
        !storyAnnounceParams['story-title'] ||
        !storyAnnounceParams['show-name'] ||
        !storyAnnounceParams['voice-profile-1']
      ) {
        notify(
          'Story Announce generation failed: Missing required parameters (story-title, show-name, voice-profile-1)',
          {
            type: 'error',
          }
        );
        return;
      }

      try {
        setStoryAnnounceLoading(true); // Use dedicated loading state

        // Create queue job and watch for completion via real-time subscription
        const result =
          await PromptService.createStoryAnnounceJob(storyAnnounceParams);

        if (!result.success || !result.jobId) {
          throw new Error(result.error || 'Failed to create Story Announce job');
        }

        console.log(
          '📢 [STORY_ANNOUNCE] Queue job created, watching for completion:',
          result.jobId
        );

        // Start watching the job via real-time subscription
        watchTtsJob(result.jobId); // Reuse TTS job watcher

        // Note: setStoryAnnounceLoading(false) will be called by the hook's onComplete/onError/onTimeout
      } catch (error) {
        console.error('❌ [STORY_ANNOUNCE] Unexpected error:', error);
        notify(
          `Story Announce generation failed: ${error instanceof Error ? error.message : 'Unexpected error occurred'}`,
          { type: 'error' }
        );
        setStoryAnnounceLoading(false);
      }

      return; // Skip the normal prompt testing flow
    }

    // For other sections, use the normal prompt testing flow
    const paramsToUse = availableParams;

    // Special handling for image generation - both show and story images
    if (
      section.id === 'imageGeneration' ||
      section.id === 'storyImageGeneration'
    ) {
      const genPromptId =
        section.id === 'storyImageGeneration' ? 'story_image' : 'show_image';
      const imagePromptKey =
        section.id === 'storyImageGeneration'
          ? 'story-image-prompt'
          : 'show-image-prompt';

      // Check if the required image prompt exists in availableParams
      const imagePrompt = availableParams[imagePromptKey];
      if (!imagePrompt || !String(imagePrompt).trim()) {
        console.error(
          `Missing ${imagePromptKey} for image generation. Available params:`,
          Object.keys(availableParams)
        );
        notify(`Image generation failed: ${imagePromptKey} is required`, {
          type: 'error',
        });
        return;
      }

      try {
        setImageLoading(true);

        // Pass all params - the edge function will use what it needs
        const result = await PromptService.createImageJob(
          genPromptId,
          availableParams
        );

        if (!result.success || !result.jobId) {
          throw new Error(
            result.error || 'Failed to create image generation job'
          );
        }

        console.log(
          '🖼️ [IMAGE] Queue job created, watching for completion:',
          result.jobId
        );

        // Start watching the job via real-time subscription
        watchImageJob(result.jobId);

        // Note: setImageLoading(false) will be called by the hook's onComplete/onError/onTimeout

        return; // Skip the normal prompt testing flow
      } catch (error) {
        console.error('❌ [IMAGE-GENERATION] Image generation error:', error);
        notify(
          `Image generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          { type: 'error' }
        );
        setImageLoading(false);
        return;
      }
    }

    // Use queue-based text generation for all text prompts
    try {
      setTextLoading(true);

      const genPromptKey = section.promptConfig?.id || section.genPromptId;
      if (!genPromptKey) {
        throw new Error(
          `Section ${section.id} missing promptConfig.id or genPromptId`
        );
      }

      const result = await PromptService.createTextJob(
        genPromptKey,
        currentPromptContent,
        currentModelName,
        paramsToUse
      );

      if (!result.success || !result.jobId) {
        throw new Error(result.error || 'Failed to create text generation job');
      }

      console.log(
        '📝 [TEXT] Queue job created, watching for completion:',
        result.jobId
      );

      // Start watching the job via real-time subscription
      // The onComplete callback will handle processing the response
      watchTextJob(result.jobId);

      // Note: setTextLoading(false) will be called by the hook's onComplete/onError/onTimeout
    } catch (error) {
      console.error(`❌ [${section.id}] Text generation error:`, error);
      notify(
        `${section.title} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );
      setTextLoading(false);
    }
  };

  const handleResponseDataChange = async (
    responseData: Record<string, unknown>
  ) => {
    // Special handling for story selection - populate topic, title, brief, takeaway
    let processedResponseData = responseData;
    if (
      section.id === 'storyIdea' &&
      responseData.selectedStory &&
      responseData.selectedStory !== ''
    ) {
      try {
        const selectedShow = sectionData.showIdeas?.selectedShow || '';
        const stories = await PromptService.getStoriesForShow(selectedShow);
        const selectedStory = stories.find(
          s => s.id === responseData.selectedStory
        );

        if (selectedStory) {
          // Populate with story data
          processedResponseData = {
            ...responseData,
            slug: selectedStory.slug || '',
            topic: selectedStory.topic || '',
            title: selectedStory.title,
            brief: selectedStory.brief || '',
            takeaway: selectedStory.takeaway || '',
            taxonomy: selectedStory.taxonomy || '',
            description: selectedStory.description || '',
            searchTerms: selectedStory.searchTerms || '',
            imagePrompt: selectedStory.imagePrompt || '',
          };

          // Populate storyOutline with the selected story's outline AND its
          // stored delivery spec + rolled form. Set them explicitly (don't
          // spread the prior storyOutline) so switching stories never leaves the
          // previous story's plan/form feeding {{story-delivery-spec}}.
          const selForm = availableForms.find(
            f => f.key === selectedStory.formKey
          );
          onDataChange('storyOutline', {
            ...sectionData.storyOutline,
            outline: selectedStory.outline || '',
            delivery_spec: selectedStory.deliverySpec ?? null,
            formKey: selectedStory.formKey || undefined,
            formInstructions: selForm?.instructions || undefined,
          });
        }
      } catch (error) {
        console.error('Failed to load story data:', error);
      }
    }

    // Special handling for show selection - populate topic and other show details
    if (
      section.id === 'showIdeas' &&
      responseData.selectedShow &&
      responseData.selectedShow !== ''
    ) {
      try {
        const shows = await PromptService.getShows();
        const selectedShow = shows.find(
          s => s.id === responseData.selectedShow
        );

        if (selectedShow) {
          // Populate with show topic and genre information
          processedResponseData = {
            ...responseData,
            topic: selectedShow.topic || '',
          };

          // Add genre information if available
          if (selectedShow.genre) {
            processedResponseData.selectedGenre = selectedShow.genre.id;
            processedResponseData.genreName = selectedShow.genre.name;
            processedResponseData.genreDescription =
              selectedShow.genre.description;
            processedResponseData.genreInstructions =
              selectedShow.genre.instructions;
          }

          // Add language information if available
          if (selectedShow.lang) {
            try {
              // Fetch language data directly to avoid race conditions with availableLanguages
              const language = await LanguageApi.getByKey(selectedShow.lang);
              if (language) {
                processedResponseData.selectedLanguage = selectedShow.lang;
                processedResponseData.languageName = language.name;
              }
            } catch (error) {
              console.error('Failed to load language data for show:', error);
              // Fallback to just setting the key
              processedResponseData.selectedLanguage = selectedShow.lang;
            }
          }

          // Update the downstream sections with show voice profile data
          onDataChange('show', {
            showSlug: selectedShow.slug,
            showName: selectedShow.slug,
            description: selectedShow.description || '',
            taxonomy: selectedShow.taxonomy || '',
            itunesCategory: selectedShow.itunesCategory || '',
            itunesSubcategory: selectedShow.itunesSubcategory || '',
            style: selectedShow.style,
            audioLogoDesc: selectedShow.audioLogoDesc || '',
            storyScope: selectedShow.storyScope || '',
            titleInstructions: selectedShow.titleInstructions || '',
            hostPersona1: selectedShow.hostPersona1 || null,
            hostPersona2: selectedShow.hostPersona2 || null,
            voiceProfile1: selectedShow.voiceProfile1 || '',
            voiceIndex1: selectedShow.voiceIndex1 ?? 0,
            voiceInstructions1: selectedShow.voiceInstructions1 || '',
            voiceProfile2: selectedShow.voiceProfile2 || '',
            voiceIndex2: selectedShow.voiceIndex2 ?? 0,
            voiceInstructions2: selectedShow.voiceInstructions2 || '',
            scriptInstructions1: selectedShow.scriptInstructions1 || '',
            scriptInstructions2: selectedShow.scriptInstructions2 || '',
            dynamics: selectedShow.dynamicsKey || '',
            musicPrompt: selectedShow.musicPrompt || '',
            imagePrompt: selectedShow.imagePrompt || '',
            tags: selectedShow.tags || '',
          });
        }
      } catch (error) {
        console.error('Failed to load show data:', error);
      }
    }

    // Special handling for genre selection - populate genre details from already-loaded data
    if (
      section.id === 'showIdeas' &&
      responseData.selectedGenre &&
      responseData.selectedGenre !== ''
    ) {
      const selectedGenre = availableGenres.find(
        g => g.id === responseData.selectedGenre
      );
      if (selectedGenre) {
        processedResponseData = {
          ...processedResponseData,
          selectedGenre: selectedGenre.id,
          genreName: selectedGenre.name,
          genreDescription: selectedGenre.description || '',
          genreInstructions: selectedGenre.instructions || '',
        };
      }
    }

    // Special handling for language selection - populate language name from already-loaded data
    if (
      section.id === 'showIdeas' &&
      responseData.selectedLanguage &&
      responseData.selectedLanguage !== ''
    ) {
      const selectedLanguage = availableLanguages.find(
        lang => lang.key === responseData.selectedLanguage
      );
      if (selectedLanguage) {
        processedResponseData = {
          ...processedResponseData,
          selectedLanguage: selectedLanguage.key,
          languageName: selectedLanguage.name,
        };
      }
    }

    // Special handling for Show Announce section - populate audio logo from selected show
    if (
      section.id === 'showAnnounce' &&
      responseData.selectedShow &&
      responseData.selectedShow !== ''
    ) {
      try {
        const shows = await PromptService.getShows();
        const selectedShow = shows.find(
          s => s.id === responseData.selectedShow
        );

        if (selectedShow) {
          // Update show section data with audio logo description
          onDataChange('show', {
            ...sectionData.show,
            audioLogoDesc: selectedShow.audioLogoDesc || '',
            showName: selectedShow.slug,
          });
        }
      } catch (error) {
        console.error('Failed to load show data for Show Announce:', error);
      }
    }

    // Update the section data with the processed response
    onDataChange(section.id, processedResponseData);
  };

  const handlePromptImprove = (improvedPrompt: string) => {
    setCurrentPromptContent(improvedPrompt);
  };

  const handleOpenCompare = () => {
    // Use the same validation logic as PromptTextArea to check for missing parameters
    const templateParams = extractTemplateParams(currentPromptContent);
    const missingParams = templateParams.filter(param => {
      const { available } = getParamAvailability(param, availableParams);
      return !available;
    });

    if (missingParams.length > 0) {
      notify(
        `Please fill in all required parameters before comparing models: ${missingParams.join(', ')}`,
        { type: 'error' }
      );
      return;
    }

    // Open the comparison dialog
    setCompareDialogOpen(true);
  };

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Show Ideas specific handlers
  const handleShowChange = async (showId: string) => {
    // When a show is selected, get the full show data to populate the topic
    if (showId) {
      try {
        const shows = await PromptService.getShows();
        const selectedShow = shows.find(show => show.id === showId);

        if (selectedShow) {
          // Update with show topic and genre data
          const updatedData: Partial<SectionData['showIdeas']> = {
            selectedShow: showId,
            topic: selectedShow.topic || '',
          };

          // Add genre information if available so selector reflects show choice
          if (selectedShow.genre) {
            updatedData.selectedGenre = selectedShow.genre.id;
            updatedData.genreName = selectedShow.genre.name;
            updatedData.genreDescription = selectedShow.genre.description;
            updatedData.genreInstructions = selectedShow.genre.instructions;
          }

          // Add language information if available so selector reflects show choice
          if (selectedShow.lang) {
            try {
              // Fetch language data directly to avoid race conditions with availableLanguages
              const language = await LanguageApi.getByKey(selectedShow.lang);
              if (language) {
                updatedData.selectedLanguage = selectedShow.lang;
                updatedData.languageName = language.name;
              }
            } catch (error) {
              console.error('Failed to load language data for show:', error);
              // Fallback to just setting the key
              updatedData.selectedLanguage = selectedShow.lang;
            }
          }
          onDataChange('showIdeas', updatedData);

          // Also update show data with all available fields from the selected show
          const showData = {
            showSlug: selectedShow.slug,
            showName: selectedShow.slug,
            description: selectedShow.description || '',
            taxonomy: selectedShow.taxonomy || '',
            itunesCategory: selectedShow.itunesCategory || '',
            itunesSubcategory: selectedShow.itunesSubcategory || '',
            style: selectedShow.style,
            audioLogoDesc: selectedShow.audioLogoDesc || '',
            storyScope: selectedShow.storyScope || '',
            titleInstructions: selectedShow.titleInstructions || '',
            hostPersona1: selectedShow.hostPersona1 || null,
            hostPersona2: selectedShow.hostPersona2 || null,
            voiceProfile1: selectedShow.voiceProfile1 || '',
            voiceIndex1: selectedShow.voiceIndex1 ?? 0,
            voiceInstructions1: selectedShow.voiceInstructions1 || '',
            voiceProfile2: selectedShow.voiceProfile2 || '',
            voiceIndex2: selectedShow.voiceIndex2 ?? 0,
            voiceInstructions2: selectedShow.voiceInstructions2 || '',
            scriptInstructions1: selectedShow.scriptInstructions1 || '',
            scriptInstructions2: selectedShow.scriptInstructions2 || '',
            // dynamics key triggers useEffect in PromptDebugger to resolve dynamicsInstructions
            dynamics: selectedShow.dynamicsKey || '',
            musicPrompt: selectedShow.musicPrompt || '',
            imagePrompt: selectedShow.imagePrompt || '',
            tags: selectedShow.tags || '',
          };
          onDataChange('show', showData);
        } else {
          // Fallback if show not found
          const currentTopicData = sectionData.showIdeas || {};
          onDataChange('showIdeas', {
            ...currentTopicData,
            selectedShow: showId,
          });
        }
      } catch (error) {
        console.error('Failed to load show data:', error);
        // Fallback to preserving existing topic data
        const currentTopicData = sectionData.showIdeas || {};
        onDataChange('showIdeas', {
          ...currentTopicData,
          selectedShow: showId,
        });
      }
    } else {
      // Clear when no show selected
      onDataChange('showIdeas', { selectedShow: '', topic: '' });
    }
  };

  const handleShowTopicChange = (topic: string) => {
    const currentData = sectionData.showIdeas || {};

    // Only update if the topic actually changed to prevent unnecessary re-renders
    if (currentData.topic !== topic) {
      const newData = { ...currentData, topic };
      onDataChange('showIdeas', newData);
    }
  };

  const handleGenreChange = async (genreId: string) => {
    const currentData = sectionData.showIdeas || {};

    if (genreId) {
      try {
        const genre = await GenreApi.getById(genreId);
        if (genre) {
          const newData = {
            ...currentData,
            selectedGenre: genreId,
            genreName: genre.name,
            genreDescription: genre.description,
            genreInstructions: genre.instructions,
          };
          onDataChange('showIdeas', newData);
        }
      } catch (error) {
        console.error('Failed to load genre data:', error);
        // Fallback to just setting the ID
        onDataChange('showIdeas', { ...currentData, selectedGenre: genreId });
      }
    } else {
      // Clear genre data
      const {
        selectedGenre,
        genreName,
        genreDescription,
        genreInstructions,
        ...restData
      } = currentData as any;
      onDataChange('showIdeas', restData);
    }
  };

  const handleLanguageChange = async (languageKey: string) => {
    console.log(
      '🌍 [LANGUAGE] handleLanguageChange called with key:',
      languageKey
    );
    const currentData = sectionData.showIdeas || {};

    if (languageKey) {
      try {
        console.log(
          '🌍 [LANGUAGE] Fetching language data for key:',
          languageKey
        );
        const language = await LanguageApi.getByKey(languageKey);
        console.log('🌍 [LANGUAGE] Received language data:', language);
        if (language) {
          const newData = {
            ...currentData,
            selectedLanguage: languageKey,
            languageName: language.name,
          };
          console.log(
            '🌍 [LANGUAGE] Setting newData with languageName:',
            language.name
          );
          onDataChange('showIdeas', newData);
        }
      } catch (error) {
        console.error('❌ [LANGUAGE] Failed to load language data:', error);
        // Fallback to just setting the key
        onDataChange('showIdeas', {
          ...currentData,
          selectedLanguage: languageKey,
        });
      }
    } else {
      // Clear language data
      console.log('🌍 [LANGUAGE] Clearing language data');
      const { selectedLanguage, languageName, ...restData } =
        currentData as any;
      onDataChange('showIdeas', restData);
    }
  };

  // Story Idea specific handlers
  const handleStoryChange = (
    storyId: string,
    storyData: { title: string; brief: string }
  ) => {
    onDataChange('storyIdea', { selectedStory: storyId, ...storyData });
  };

  // Script prepopulation removed — now handled by language-aware useEffect in PromptDebugger.tsx
  // which fetches from story_translations for the correct language

  // Load stories for dropdown when show is selected (for storyIdea section)
  useEffect(() => {
    const selectedShow = sectionData.showIdeas?.selectedShow;
    if (section.id === 'storyIdea' && selectedShow) {
      const loadStoriesForDropdown = async () => {
        try {
          const stories = await PromptService.getStoriesForShow(selectedShow);
          setAvailableStories(
            stories.map(story => ({ id: story.id, title: story.title }))
          );
        } catch (error) {
          console.error('Failed to load stories for dropdown:', error);
          setAvailableStories([]);
        }
      };
      loadStoriesForDropdown();
    } else {
      setAvailableStories([]);
    }
  }, [section.id, sectionData.showIdeas?.selectedShow]);

  // Load shows for dropdown (for showIdeas section)
  useEffect(() => {
    if (section.id === 'showIdeas') {
      const loadShowsForDropdown = async () => {
        try {
          const shows = await PromptService.getShows();
          setAvailableShows(
            shows.map(show => ({
              id: show.id,
              name: show.name,
              slug: show.slug,
              topic: show.topic,
              description: show.description,
              taxonomy: show.taxonomy,
              itunesCategory: show.itunesCategory,
              itunesSubcategory: show.itunesSubcategory,
              style: show.style,
              genreId: show.genreId,
              musicPrompt: show.musicPrompt,
              imagePrompt: show.imagePrompt,
              hostPersona1: show.hostPersona1,
              hostPersona2: show.hostPersona2,
              dynamicsKey: show.dynamicsKey,
              voiceProfile1: show.voiceProfile1,
              voiceIndex1: show.voiceIndex1,
              voiceInstructions1: show.voiceInstructions1,
              voiceProfile2: show.voiceProfile2,
              voiceIndex2: show.voiceIndex2,
              voiceInstructions2: show.voiceInstructions2,
              scriptInstructions1: show.scriptInstructions1,
              scriptInstructions2: show.scriptInstructions2,
              audioLogoDesc: show.audioLogoDesc,
              storyScope: show.storyScope,
              titleInstructions: show.titleInstructions,
              tags: show.tags,
              lang: show.lang,
            }))
          );
        } catch (error) {
          console.error('Failed to load shows for dropdown:', error);
          setAvailableShows([]);
          setExistingTopics('Error loading existing shows');
        }
      };
      loadShowsForDropdown();
    }
  }, [section.id]);

  useEffect(() => {
    if (section.id !== 'showIdeas') {
      return;
    }

    const selectedGenreId = sectionData.showIdeas?.selectedGenre || '';

    if (availableShows.length === 0) {
      setExistingTopics('No existing shows');
      return;
    }

    const topics = availableShows
      .filter(show => !selectedGenreId || show.genreId === selectedGenreId)
      .filter(show => show.topic && show.topic.trim())
      .map(show => show.topic.trim())
      .slice(0, 10); // keep prompt tight

    if (topics.length > 0) {
      setExistingTopics(topics.join('\n- '));
    } else {
      setExistingTopics('No existing shows for selected genre');
    }
  }, [section.id, availableShows, sectionData.showIdeas?.selectedGenre]);

  // Load genres for dropdown (needed for params across sections)
  useEffect(() => {
    const loadGenresForDropdown = async () => {
      try {
        const genres = await GenreApi.getAll();
        setAvailableGenres(
          genres.map(genre => ({
            id: genre.id,
            name: genre.name,
            description: genre.description,
            instructions: genre.instructions,
          }))
        );
      } catch (error) {
        console.error('Failed to load genres for dropdown:', error);
        setAvailableGenres([]);
      }
    };
    loadGenresForDropdown();
  }, []);

  // #766: load narrative forms for the Story Outline form selector
  useEffect(() => {
    if (section.id !== 'storyOutline') return;
    const loadForms = async () => {
      try {
        const forms = await listAllNarrativeForms();
        setAvailableForms(forms.filter(f => f.enabled));
      } catch (error) {
        console.error('Failed to load narrative forms:', error);
        setAvailableForms([]);
      }
    };
    loadForms();
  }, [section.id]);

  // #766: forms compatible with the selected show — mirrors selectForm in
  // gen-story-outline: enabled AND (genre tag null OR includes genre name)
  // AND (dynamics tag null OR includes the show's dynamics key).
  // With no show/genre selected yet, all enabled forms are offered (sandbox).
  const compatibleForms = useMemo(() => {
    const genreName = sectionData.showIdeas?.genreName || '';
    const dynamicsKey = sectionData.show?.dynamics || '';
    return availableForms.filter(f => {
      const genreOk =
        !genreName ||
        !f.compatibleGenres ||
        f.compatibleGenres.length === 0 ||
        f.compatibleGenres.includes(genreName);
      const dynOk =
        !dynamicsKey ||
        !f.compatibleDynamics ||
        f.compatibleDynamics.length === 0 ||
        f.compatibleDynamics.includes(dynamicsKey);
      return genreOk && dynOk;
    });
  }, [availableForms, sectionData.showIdeas?.genreName, sectionData.show?.dynamics]);

  // Mirrors the formless default in gen-story-outline (formKey 'none').
  const NO_FORM_INSTRUCTIONS =
    'No specific narrative form is assigned — structure the telling however best serves the material.';

  const handleFormSelect = useCallback(
    (formKey: string) => {
      const currentData = sectionData.storyOutline || {};
      if (!formKey || formKey === 'none') {
        onDataChange('storyOutline', {
          ...currentData,
          formKey: 'none',
          formInstructions: NO_FORM_INSTRUCTIONS,
        });
        return;
      }
      const form = availableForms.find(f => f.key === formKey);
      if (form) {
        onDataChange('storyOutline', {
          ...currentData,
          formKey: form.key,
          formInstructions: form.instructions,
        });
      }
    },
    [sectionData.storyOutline, availableForms, onDataChange, NO_FORM_INSTRUCTIONS]
  );

  // Roll a random compatible form — same coded roll as the pipeline
  // (minus the recent-forms exclusion, which needs a concrete show history).
  const handleFormRoll = useCallback(() => {
    if (compatibleForms.length === 0) {
      handleFormSelect('none');
      return;
    }
    const chosen =
      compatibleForms[Math.floor(Math.random() * compatibleForms.length)];
    handleFormSelect(chosen?.key || 'none');
  }, [compatibleForms, handleFormSelect]);

  // Load languages for dropdown (needed for params across sections)
  useEffect(() => {
    const loadLanguagesForDropdown = async () => {
      try {
        const languages = await LanguageApi.getAll();
        setAvailableLanguages(
          languages.map(language => ({
            key: language.key,
            name: language.name,
          }))
        );
      } catch (error) {
        console.error('Failed to load languages for dropdown:', error);
        setAvailableLanguages([]);
      }
    };
    loadLanguagesForDropdown();
  }, []);

  // Genre is input-driven; do not infer from responses
  return (
    <Paper
      elevation={2}
      sx={{
        padding: 3,
        width: '100%',
        minWidth: 0, // Allow shrinking
        overflow: 'hidden', // Prevent content overflow
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderRadius: 1,
            marginX: -1,
            paddingX: 1,
          },
        }}
        onClick={handleToggleExpanded}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', flex: 1 }}>
          {section.title}
        </Typography>
        <IconButton size="small" sx={{ ml: 1 }}>
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Divider sx={{ marginY: 2 }} />

        <Box
          sx={{ display: 'flex', gap: 2.5, minWidth: 0, flexWrap: 'nowrap' }}
        >
          {/* Left Panel - Prompt */}
          <Box sx={{ minWidth: 0, flex: '1 1 50%' }}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
              }}
            >
              {section.id !== 'tts' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      marginBottom: 2,
                    }}
                  >
                    <Typography variant="h6">
                      {section.hasPrompt ? 'Prompt' : 'Configuration'}
                    </Typography>
                    <ModelPicker
                      selectedModel={currentModelName}
                      originalModel={originalModelName}
                      onChange={handleModelChange}
                      onReset={handleModelReset}
                      onSave={handleModelSave}
                      availableModels={availableModels}
                    />
                    {/* Compare Icon - only show when model is not modified and section has a prompt */}
                    {section.hasPrompt &&
                      currentModelName === originalModelName && (
                        <IconButton
                          size="small"
                          onClick={handleOpenCompare}
                          sx={{ color: 'primary.main' }}
                          title="Compare models"
                        >
                          <GitCompareArrows size={20} />
                        </IconButton>
                      )}
                  </Box>

                  {/* Debug Mode Alert */}
                  {debugModeInfo.isEnabled && debugModeInfo.debugPreset && (
                    <Alert
                      severity="warning"
                      icon={<Bug size={18} />}
                      sx={{ mb: 2, py: 0.5 }}
                    >
                      <Typography variant="body2">
                        <strong>Debug Mode</strong> - Using{' '}
                        {debugModeInfo.debugPreset.name}
                      </Typography>
                    </Alert>
                  )}
                </>
              )}

              {section.id === 'tts' ? (
                <Box sx={{ flex: 1 }}>
                  {/* Title and Model Picker side by side */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      marginBottom: 2,
                    }}
                  >
                    <Typography variant="h6">TTS Configuration</Typography>
                    <ModelPicker
                      selectedModel={currentModelName}
                      originalModel={originalModelName}
                      onChange={handleModelChange}
                      onReset={handleModelReset}
                      onSave={handleModelSave}
                      availableModels={availableModels}
                    />
                  </Box>

                  {/* Debug Mode Alert for TTS */}
                  {debugModeInfo.isEnabled && debugModeInfo.debugPreset && (
                    <Alert
                      severity="warning"
                      icon={<Bug size={18} />}
                      sx={{ mb: 2, py: 0.5 }}
                    >
                      <Typography variant="body2">
                        <strong>Debug Mode</strong> - Using{' '}
                        {debugModeInfo.debugPreset.name}
                      </Typography>
                    </Alert>
                  )}

                  {/* Script Content Display */}
                  <Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        padding: 2,
                        backgroundColor: 'grey.50',
                        maxHeight: '200px',
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {sectionData.script?.script ||
                          'No script content available. Generate a script first.'}
                      </Typography>
                    </Paper>

                    {/* Generate Audio Button */}
                    <Box
                      sx={{
                        marginTop: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRun}
                        disabled={ttsLoading || !sectionData.script?.script}
                        sx={{ minWidth: '120px' }}
                      >
                        {ttsLoading ? 'Running...' : 'Run'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : section.hasPrompt ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {section.id === 'storyIdea' ? (
                    <StoryIdeaSection>
                      <Box>
                        {/* Prompt Content */}
                        <Box sx={{ flex: 1 }}>
                          <PromptTextArea
                            content={currentPromptContent}
                            originalContent={originalPromptContent}
                            requiredParams={
                              section.promptConfig?.requiredParams || []
                            }
                            availableParams={availableParams}
                            onChange={handlePromptChange}
                            onReset={handlePromptReset}
                            onSave={handlePromptSave}
                            onRun={handleRun}
                            isRunning={textLoading}
                            onImprove={handlePromptImprove}
                            isImproving={promptDebug.loading}
                            hasValidationError={false}
                            onOpenSyntaxGuide={onOpenSyntaxGuide}
                          />
                        </Box>
                      </Box>
                    </StoryIdeaSection>
                  ) : (
                    <Box>
                      {/* Section inputs — values the pipeline supplies from
                          code/DB, chosen here by the user (above the prompt,
                          since they are inputs, not generation outputs). */}
                      {section.id === 'showIdeas' && (
                        <Box sx={{ marginBottom: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel id="show-ideas-genre-label">
                              Genre
                            </InputLabel>
                            <Select
                              labelId="show-ideas-genre-label"
                              label="Genre"
                              value={sectionData.showIdeas?.selectedGenre || ''}
                              onChange={e => handleGenreChange(e.target.value)}
                            >
                              <MenuItem value="">
                                <em>Select a genre</em>
                              </MenuItem>
                              {availableGenres.map(genre => (
                                <MenuItem key={genre.id} value={genre.id}>
                                  {genre.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                      {section.id === 'storyOutline' && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            marginBottom: 2,
                          }}
                        >
                          <FormControl size="small" sx={{ minWidth: 300 }}>
                            <InputLabel id="story-outline-form-label">
                              Narrative Form
                            </InputLabel>
                            <Select
                              labelId="story-outline-form-label"
                              label="Narrative Form"
                              value={
                                sectionData.storyOutline?.formKey &&
                                sectionData.storyOutline.formKey !== 'none'
                                  ? sectionData.storyOutline.formKey
                                  : sectionData.storyOutline?.formKey === 'none'
                                    ? 'none'
                                    : ''
                              }
                              onChange={e => handleFormSelect(e.target.value)}
                            >
                              <MenuItem value="none">
                                <em>No form (formless default)</em>
                              </MenuItem>
                              {compatibleForms.map(form => (
                                <MenuItem key={form.key} value={form.key}>
                                  {form.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            onClick={handleFormRoll}
                            title="Roll a random compatible form (like the pipeline does)"
                            sx={{ color: 'primary.main' }}
                          >
                            <Dices size={20} />
                          </IconButton>
                          <Typography variant="caption" color="text.secondary">
                            {sectionData.showIdeas?.genreName
                              ? `${compatibleForms.length} forms match "${sectionData.showIdeas.genreName}"`
                              : `${compatibleForms.length} forms (select a show to filter by genre)`}
                          </Typography>
                        </Box>
                      )}
                      {/* Prompt Content */}
                      <Box sx={{ flex: 1 }}>
                        <PromptTextArea
                          content={currentPromptContent}
                          originalContent={originalPromptContent}
                          requiredParams={
                            section.promptConfig?.requiredParams || []
                          }
                          availableParams={availableParams}
                          onChange={handlePromptChange}
                          onReset={handlePromptReset}
                          onSave={handlePromptSave}
                          onRun={handleRun}
                          isRunning={
                            section.id === 'imageGeneration' ||
                            section.id === 'storyImageGeneration'
                              ? imageLoading
                              : section.id === 'showAnnounce'
                                ? showAnnounceLoading
                                : section.id === 'storyAnnounce'
                                  ? storyAnnounceLoading
                                  : textLoading
                          }
                          onImprove={handlePromptImprove}
                          isImproving={promptDebug.loading}
                          hasValidationError={false}
                          onOpenSyntaxGuide={onOpenSyntaxGuide}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ flex: 1 }}>
                  {section.id === 'showIdeas' ? (
                    <ShowIdeaSection
                      selectedShow={sectionData.showIdeas?.selectedShow || ''}
                      topic={sectionData.showIdeas?.topic || ''}
                      selectedGenre={
                        (sectionData.showIdeas as any)?.selectedGenre || ''
                      }
                      selectedLanguage={
                        (sectionData.showIdeas as any)?.selectedLanguage || ''
                      }
                      onShowChange={handleShowChange}
                      onTopicChange={handleShowTopicChange}
                      onGenreChange={handleGenreChange}
                      onLanguageChange={handleLanguageChange}
                      responseData={sectionData.showIdeas}
                    >
                      {/* Prompt Content */}
                      <PromptTextArea
                        content={currentPromptContent}
                        originalContent={originalPromptContent}
                        requiredParams={
                          section.promptConfig?.requiredParams || []
                        }
                        availableParams={availableParams}
                        onChange={handlePromptChange}
                        onReset={handlePromptReset}
                        onSave={handlePromptSave}
                        onRun={handleRun}
                        isRunning={textLoading}
                        onImprove={handlePromptImprove}
                        isImproving={promptDebug.loading}
                        hasValidationError={false}
                        onOpenSyntaxGuide={onOpenSyntaxGuide}
                      />
                    </ShowIdeaSection>
                  ) : (
                    <Box
                      sx={{
                        padding: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        flex: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        This section uses manual configuration only.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Panel - Response */}
          <Box sx={{ minWidth: 0, flex: '1 1 50%' }}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Response
              </Typography>

              <Box sx={{ flex: 1 }}>
                {section.id === 'tts' || section.id === 'showAnnounce' || section.id === 'storyAnnounce' ? (
                  // Native HTML audio player for TTS, Show Announce, and Story Announce sections
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '200px',
                    }}
                  >
                    {(section.id === 'tts' && sectionData.tts?.audioUrl) ||
                    (section.id === 'showAnnounce' &&
                      sectionData.showAnnounce?.audioUrl) ||
                    (section.id === 'storyAnnounce' &&
                      sectionData.storyAnnounce?.audioUrl) ? (
                      <audio
                        controls
                        src={`${
                          section.id === 'tts' ? sectionData.tts?.audioUrl :
                          section.id === 'showAnnounce' ? sectionData.showAnnounce?.audioUrl :
                          sectionData.storyAnnounce?.audioUrl
                        }${(
                          section.id === 'tts' ? sectionData.tts?.audioUrl :
                          section.id === 'showAnnounce' ? sectionData.showAnnounce?.audioUrl :
                          sectionData.storyAnnounce?.audioUrl
                        )?.includes('?') ? '&' : '?'}t=${Date.now()}`}
                        style={{ width: '100%', maxWidth: '400px' }}
                      >
                        <track kind="captions" src="" label="Audio captions" />
                        Your browser does not support the audio element.
                      </audio>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click &lsquo;Run&rsquo; to generate
                      </Typography>
                    )}
                  </Box>
                ) : section.id === 'imageGeneration' ||
                  section.id === 'storyImageGeneration' ? (
                  // Image display for Image Generation sections (show and story)
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '200px',
                    }}
                  >
                    {(() => {
                      const imageData =
                        section.id === 'storyImageGeneration'
                          ? sectionData.storyImageGeneration
                          : sectionData.imageGeneration;
                      const imageUrl = imageData?.imageUrl;
                      const altText =
                        section.id === 'storyImageGeneration'
                          ? 'Generated story image'
                          : 'Generated show image';

                      return imageUrl ? (
                        <Box
                          component="img"
                          src={(() => {
                            // Handle both storage paths and URLs
                            // If it's a simple path (e.g., "temp/debug-console-image.png"), use it directly
                            if (
                              !imageUrl.includes('://') &&
                              !imageUrl.startsWith('/')
                            ) {
                              const { data } = supabase.storage
                                .from('media')
                                .getPublicUrl(imageUrl);
                              return `${data.publicUrl}?t=${Date.now()}`;
                            }
                            // For URLs, add cache busting
                            return addCacheBusting(imageUrl);
                          })()}
                          alt={altText}
                          sx={{
                            maxWidth: 300,
                            height: 'auto',
                            border: 1,
                            borderColor: 'grey.300',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => {
                            // Open direct storage URL for paths
                            if (
                              !imageUrl.includes('://') &&
                              !imageUrl.startsWith('/')
                            ) {
                              const { data } = supabase.storage
                                .from('media')
                                .getPublicUrl(imageUrl);
                              window.open(data.publicUrl, '_blank');
                            } else {
                              window.open(imageUrl, '_blank');
                            }
                          }}
                          title="Click to open full size in new tab"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Click &lsquo;Run&rsquo; to generate
                        </Typography>
                      );
                    })()}
                  </Box>
                ) : (
                  <ResponseDisplay
                    fields={section.responseFields}
                    response={promptDebug.testResponse || undefined}
                    data={sectionData[section.id] || {}}
                    onChange={handleResponseDataChange}
                    dropdownOptions={(() => {
                      const options =
                        section.id === 'storyIdea' &&
                        sectionData.showIdeas?.selectedShow
                          ? {
                              selectedStory: [
                                { value: '', label: 'Populate from a story' },
                                ...availableStories.map(story => ({
                                  value: story.id,
                                  label: story.title,
                                })),
                              ],
                              selectedShow: [],
                              selectedGenre: [],
                              selectedLanguage: [],
                            }
                          : section.id === 'showIdeas'
                            ? {
                                selectedShow: [
                                  { value: '', label: 'Populate from a show' },
                                  ...availableShows.map(show => ({
                                    value: show.id,
                                    label: show.name,
                                  })),
                                ],
                                selectedGenre: [
                                  { value: '', label: 'Select a genre' },
                                  ...availableGenres.map(genre => ({
                                    value: genre.id,
                                    label: genre.name,
                                  })),
                                ],
                                selectedLanguage: [
                                  { value: '', label: 'Select a language' },
                                  ...availableLanguages.map(language => ({
                                    value: language.key,
                                    label: language.name,
                                  })),
                                ],
                                selectedStory: [],
                              }
                            : section.id === 'showAnnounce'
                              ? {
                                  // Show Announce section needs show dropdown
                                  selectedShow: [
                                    { value: '', label: 'Select a show' },
                                    ...availableShows.map(show => ({
                                      value: show.id,
                                      label: show.name,
                                    })),
                                  ],
                                  selectedGenre: [],
                                  selectedLanguage: [],
                                  selectedStory: [],
                                }
                              : {
                                  selectedShow: [],
                                  selectedGenre: [],
                                  selectedLanguage: [],
                                  selectedStory: [],
                                };

                      return options;
                    })()}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* Model Comparison Dialog */}
      <ModelComparisonDialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        promptId={genPromptKey}
        promptTemplate={currentPromptContent}
        parameters={availableParams}
        sectionTitle={section.title}
        availableModels={availableModels}
      />
    </Paper>
  );
};
